// Listado admin de sesiones del rango con asignación/desasignación de profesor.
// Permite aplicar override sobre el titular del horario para una sesión concreta
// (sustituciones puntuales). Backend ya enviaba emails al asignar/desasignar
// desde SS7.7; esta pantalla es la UI que faltaba.

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle, Loader2, ChevronLeft, ChevronRight, X, UserCheck, UserMinus, Users,
} from 'lucide-react';
import { createPortal } from 'react-dom';

import {
  listSesiones, listProfesores, asignarProfesorSesion, desasignarProfesorSesion,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

function rangoDelMes(periodo) {
  const [y, m] = periodo.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    desde: `${y}-${String(m).padStart(2, '0')}-01`,
    hasta: `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  };
}

function periodoActual() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function periodoOffset(periodo, delta) {
  const [y, m] = periodo.split('-').map(Number);
  const total = (y * 12 + (m - 1)) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

function nombrePeriodo(periodo, lang) {
  const [y, m] = periodo.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  const localeMap = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  const locale = localeMap[lang] || 'es-ES';
  const f = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d);
  return f.charAt(0).toLocaleUpperCase() + f.slice(1);
}

export default function AdminSesiones() {
  const { t, i18n } = useTranslation();
  const [periodo, setPeriodo] = useState(periodoActual());
  const [sesiones, setSesiones] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null); // {sesion, profesor_id, notas_admin}
  const [busy, setBusy] = useState(false);
  const [filtroProf, setFiltroProf] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { desde, hasta } = rangoDelMes(periodo);
      const [r, p] = await Promise.all([
        listSesiones({ desde, hasta }),
        listProfesores(),
      ]);
      setSesiones(r.data || []);
      setProfesores(p.data || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [periodo]);
  useEffect(() => { cargar(); }, [cargar]);

  const filtradas = filtroProf
    ? sesiones.filter((s) => (s.profesor_efectivo?.id || s.titular?.id) === filtroProf)
    : sesiones;

  const onAsignar = async () => {
    if (!editar?.profesor_id) return;
    setBusy(true);
    try {
      await asignarProfesorSesion(editar.sesion.id, {
        profesor_id: editar.profesor_id,
        notas_admin: editar.notas_admin || null,
      });
      setEditar(null);
      await cargar();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e.message || t('admin.error_generico')));
    } finally { setBusy(false); }
  };

  const onDesasignar = async (sesion) => {
    const titularNombre = sesion.titular?.nombre_publico || '—';
    if (!window.confirm(t('admin.sesiones.confirmar_quitar', { titular: titularNombre }))) return;
    setBusy(true);
    try {
      await desasignarProfesorSesion(sesion.id);
      await cargar();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e.message || t('admin.error_generico')));
    } finally { setBusy(false); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.sesiones.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.sesiones.sub')}</p>
        </div>
      </header>

      <div className="pagos-periodo-bar">
        <button type="button" className="btn-icon" onClick={() => setPeriodo(periodoOffset(periodo, -1))} aria-label={t('admin.pagos_profesor.mes_anterior')}>
          <ChevronLeft size={18} />
        </button>
        <div className="pagos-periodo-label">
          <span className="pagos-periodo-nombre">{nombrePeriodo(periodo, i18n.language)}</span>
          <span className="pagos-periodo-iso">{periodo}</span>
        </div>
        <button type="button" className="btn-icon" onClick={() => setPeriodo(periodoOffset(periodo, 1))} aria-label={t('admin.pagos_profesor.mes_siguiente')}>
          <ChevronRight size={18} />
        </button>
        <button type="button" className="btn-secondary btn-mini" onClick={() => setPeriodo(periodoActual())}>
          {t('admin.pagos_profesor.hoy')}
        </button>
      </div>

      <div className="admin-filtros admin-filtros-inline">
        <label className="admin-filtro-campo">
          <span>{t('admin.sesiones.filtro_profesor')}</span>
          <select value={filtroProf} onChange={(e) => setFiltroProf(e.target.value)}>
            <option value="">{t('admin.sesiones.todos_profesores')}</option>
            {profesores.map((p) => (
              <option key={p.id} value={p.id}>{p.nombre_publico}</option>
            ))}
          </select>
        </label>
      </div>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}

      {!loading && !error && filtradas.length === 0 && (
        <div className="admin-empty">{t('admin.sesiones.vacio')}</div>
      )}

      {!loading && !error && filtradas.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.sesiones.col.fecha')}</th>
              <th>{t('admin.sesiones.col.hora')}</th>
              <th>{t('admin.sesiones.col.clase')}</th>
              <th>{t('admin.sesiones.col.profesor')}</th>
              <th className="num">{t('admin.sesiones.col.alumnos')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtradas.map((s) => {
              const efectivoONominal = s.profesor_efectivo || s.titular;
              return (
                <tr key={s.id}>
                  <td>{s.fecha_clase}</td>
                  <td>{s.hora_inicio ? s.hora_inicio.slice(0, 5) : '—'}</td>
                  <td>{s.clase_tipo?.nombre_es || '—'}</td>
                  <td>
                    {efectivoONominal?.nombre_publico || '—'}
                    {s.tiene_override && (
                      <span className="profesor-pill-asignado" style={{ marginLeft: 8 }}>
                        {t('admin.sesiones.override')}
                      </span>
                    )}
                    {s.tiene_override && s.titular && (
                      <div className="dim" style={{ display: 'inline-block', marginLeft: 8 }}>
                        ({t('admin.sesiones.titular_era')} {s.titular.nombre_publico})
                      </div>
                    )}
                  </td>
                  <td className="num">
                    <span className="dim" style={{ display: 'inline' }}>
                      <Users size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                      {s.num_alumnos_inscritos}
                    </span>
                  </td>
                  <td className="admin-acciones">
                    <button
                      type="button"
                      className="btn-mini btn-secondary"
                      onClick={() => setEditar({
                        sesion: s,
                        profesor_id: efectivoONominal?.id || '',
                        notas_admin: '',
                      })}
                    >
                      <UserCheck size={14} />
                      {s.tiene_override ? t('admin.sesiones.accion.reasignar') : t('admin.sesiones.accion.asignar')}
                    </button>
                    {s.tiene_override && (
                      <button
                        type="button"
                        className="btn-mini"
                        onClick={() => onDesasignar(s)}
                      >
                        <UserMinus size={14} />
                        {t('admin.sesiones.accion.quitar_override')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {editar && (
        <ModalAsignar
          editar={editar}
          profesores={profesores}
          busy={busy}
          onChange={(patch) => setEditar((e) => ({ ...e, ...patch }))}
          onCerrar={() => !busy && setEditar(null)}
          onConfirmar={onAsignar}
        />
      )}
    </div>
  );
}

function ModalAsignar({ editar, profesores, busy, onChange, onCerrar, onConfirmar }) {
  const { t } = useTranslation();
  const sesion = editar.sesion;
  const titular = sesion.titular;

  return createPortal(
    <div className="modal-backdrop" onClick={onCerrar}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">
          <UserCheck size={20} style={{ marginRight: 8 }} />
          {t('admin.sesiones.modal.titulo')}
        </h2>

        <div className="pagos-modal-resumen">
          <div>
            <span className="dim">{t('admin.sesiones.modal.fecha')}</span>
            <strong>{sesion.fecha_clase}{sesion.hora_inicio ? ` · ${sesion.hora_inicio.slice(0, 5)}` : ''}</strong>
          </div>
          <div>
            <span className="dim">{t('admin.sesiones.modal.clase')}</span>
            <strong>{sesion.clase_tipo?.nombre_es || '—'}</strong>
          </div>
          <div>
            <span className="dim">{t('admin.sesiones.modal.titular')}</span>
            <strong>{titular?.nombre_publico || '—'}</strong>
          </div>
          <div>
            <span className="dim">{t('admin.sesiones.modal.alumnos')}</span>
            <strong>{sesion.num_alumnos_inscritos}</strong>
          </div>
        </div>

        <div className="admin-form-grid">
          <div className="modal-campo full">
            <label>{t('admin.sesiones.modal.nuevo_profesor')}</label>
            <select
              value={editar.profesor_id}
              onChange={(e) => onChange({ profesor_id: e.target.value })}
              required
            >
              <option value="">—</option>
              {profesores.filter((p) => p.activo).map((p) => (
                <option key={p.id} value={p.id}>{p.nombre_publico}</option>
              ))}
            </select>
          </div>
          <div className="modal-campo full">
            <label>{t('admin.sesiones.modal.notas')}</label>
            <textarea
              rows={2}
              value={editar.notas_admin}
              onChange={(e) => onChange({ notas_admin: e.target.value })}
              placeholder={t('admin.sesiones.modal.notas_placeholder')}
              maxLength={500}
            />
          </div>
        </div>

        <p className="admin-form-hint">{t('admin.sesiones.modal.hint_email')}</p>

        <div className="modal-acciones">
          <button type="button" className="btn-secondary" onClick={onCerrar} disabled={busy}>
            {t('comun.cancelar')}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={onConfirmar}
            disabled={busy || !editar.profesor_id}
          >
            {busy ? <Loader2 size={16} className="spin" /> : t('admin.sesiones.modal.confirmar')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
